/* Native elements survive Shopify section reloads without duplicate listeners. */
(() => {
  if (customElements.get('halloween-reel')) return;
  class HalloweenReel extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-reel-stage]');
      if (!this.stage) return;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      this.originals = [...this.stage.children];
      if (!this.originals.length) return;
      this.offset = 0;
      this.velocity = 0;
      this.drag = null;
      this.visible = true;
      this.manualPause = this.dataset.autoplay !== 'true';
      this.focusPause = false;
      this.hoverPause = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 820px)');
      this.tick = this.tick.bind(this);
      this.layout = this.layout.bind(this);
      this.stage.addEventListener('pointerdown', e => this.down(e), { signal: this.signal });
      window.addEventListener('pointermove', e => this.move(e), { signal: this.signal, passive: false });
      window.addEventListener('pointerup', e => this.up(e), { signal: this.signal });
      window.addEventListener('pointercancel', e => this.up(e), { signal: this.signal });
      this.stage.addEventListener('click', e => {
        if (this.suppressClick) { e.preventDefault(); e.stopImmediatePropagation(); this.suppressClick = false; }
      }, { capture: true, signal: this.signal });
      this.stage.addEventListener('dragstart', e => e.preventDefault(), { signal: this.signal });
      this.stage.addEventListener('mouseenter', () => { this.hoverPause = this.dataset.pauseHover === 'true'; }, { signal: this.signal });
      this.stage.addEventListener('mouseleave', () => { this.hoverPause = false; this.start(); }, { signal: this.signal });
      this.stage.addEventListener('focusin', e => {
        this.focusPause = true;
        const card = e.target.closest('[data-reel-card]');
        if (card && this.cards.includes(card)) this.center(this.cards.indexOf(card));
      }, { signal: this.signal });
      this.stage.addEventListener('focusout', e => {
        if (!this.stage.contains(e.relatedTarget)) { this.focusPause = false; this.start(); }
      }, { signal: this.signal });
      this.querySelectorAll('[data-step]').forEach(button => button.addEventListener('click', () => {
        this.offset += Number(button.dataset.step) * this.pitch;
        this.velocity = 0;
        this.draw();
      }, { signal: this.signal }));
      this.pauseButton = this.querySelector('[data-pause]');
      this.pauseButton?.addEventListener('click', () => {
        this.manualPause = !this.manualPause;
        this.velocity = 0;
        this.updatePause();
        this.start();
      }, { signal: this.signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop(); else this.start();
      }, { signal: this.signal });
      document.addEventListener('shopify:block:select', e => {
        const card = e.target.closest('[data-reel-card]');
        if (card && this.originals.includes(card)) { this.editorPause = true; this.center(this.cards.indexOf(card)); }
      }, { signal: this.signal });
      document.addEventListener('shopify:block:deselect', () => { this.editorPause = false; this.start(); }, { signal: this.signal });
      this.reduced.addEventListener('change', () => { this.velocity = 0; this.updatePause(); this.start(); }, { signal: this.signal });
      this.resize = new ResizeObserver(this.layout);
      this.resize.observe(this.stage);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.start(); else this.stop();
      }, { rootMargin: '80px' });
      this.intersection.observe(this);
      this.classList.add('is-ready');
      this.layout();
      this.updatePause();
    }
    disconnectedCallback() {
      this.stop();
      this.abort?.abort();
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.stage?.querySelectorAll('[data-reel-clone]').forEach(card => card.remove());
      this.classList.remove('is-ready');
    }
    layout() {
      this.width = this.stage.clientWidth;
      if (!this.width) return;
      this.cardWidth = Number(this.mobile.matches ? this.dataset.mobileWidth : this.dataset.cardWidth);
      this.cardHeight = Number(this.mobile.matches ? this.dataset.mobileHeight : this.dataset.cardHeight);
      this.gap = Number(this.dataset.gap);
      this.pitch = this.cardWidth + this.gap;
      this.curve = Number(this.dataset.curve) / 100;
      this.stage.querySelectorAll('[data-reel-clone]').forEach(card => card.remove());
      const needed = Math.max(this.originals.length, Math.ceil((this.width + this.pitch * 4) / this.pitch));
      for (let i = this.originals.length; i < needed; i++) {
        const clone = this.originals[i % this.originals.length].cloneNode(true);
        clone.dataset.reelClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[data-shopify-editor-block]').forEach(el => el.removeAttribute('data-shopify-editor-block'));
        clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        clone.querySelectorAll('a,button,input').forEach(el => el.tabIndex = -1);
        this.stage.append(clone);
      }
      this.cards = [...this.stage.children];
      this.total = this.cards.length * this.pitch;
      this.draw();
      this.start();
    }
    updatePause() {
      if (!this.pauseButton) return;
      this.pauseButton.hidden = this.reduced.matches;
      this.pauseButton.setAttribute('aria-pressed', String(this.manualPause));
      this.pauseButton.textContent = this.manualPause ? '▶ Play motion' : 'Ⅱ Pause motion';
    }
    start() {
      if (!this.frame && this.visible && !document.hidden && this.isConnected) {
        this.previous = performance.now();
        this.frame = requestAnimationFrame(this.tick);
      }
    }
    stop() { cancelAnimationFrame(this.frame); this.frame = null; }
    tick(now) {
      this.frame = null;
      const dt = Math.min((now - this.previous) / 1000, .05);
      this.previous = now;
      const paused = this.manualPause || this.hoverPause || this.focusPause || this.editorPause || this.reduced.matches;
      if (!this.drag) {
        if (!paused) this.offset -= Number(this.dataset.speed) * dt;
        if (!this.reduced.matches && !this.focusPause && !this.editorPause) {
          this.offset += this.velocity * dt;
          this.velocity *= Math.exp(-4.5 * dt);
          if (Math.abs(this.velocity) < .5) this.velocity = 0;
        }
      }
      this.draw();
      if (!document.hidden && this.visible && (!paused || this.velocity || this.drag)) this.frame = requestAnimationFrame(this.tick);
    }
    draw() {
      if (!this.cards || !this.total) return;
      this.offset = ((this.offset % this.total) + this.total) % this.total;
      const half = this.width / 2;
      const maxH = this.cardHeight * (1 + .32 * this.curve);
      this.cards.forEach((card, i) => {
        const t = ((i * this.pitch + this.offset + this.total / 2) % this.total) - this.total / 2;
        const d = Math.min(Math.abs(t) / Math.max(half, this.pitch), 1.45);
        const w = this.cardWidth * (1 + .12 * d * d * this.curve);
        const x = half + t * (1 + .025 * d * d * this.curve);
        const edgeHeight = edge => this.cardHeight * (1 + .30 * Math.pow(Math.min(Math.abs(edge) / Math.max(half, 1), 1), 2.6) * this.curve);
        const left = edgeHeight(t - w / 2), right = edgeHeight(t + w / 2);
        const h = Math.max(left, right), yl = (h - left) / 2, yr = (h - right) / 2;
        card.style.width = `${w}px`;
        const tilt = Math.max(-1, Math.min(1, t / Math.max(half, 1))) * Number(this.dataset.tilt || 0);
        card.style.transform = `translate3d(${x-w/2}px,${22+(maxH-h)/2}px,0) rotate(${tilt}deg)`;
        const media = card.querySelector('.hs-card__media');
        media.style.height = `${h}px`;
        // Project the entire image and its rounded frame into the curved shape.
        // The vertical edges share a center, so this homography has an exact
        // closed form and needs no library or per-frame geometry measurements.
        const ratio = left / right;
        media.style.transform = `matrix3d(${ratio},${(yr * ratio - yl) / w},0,${(ratio - 1) / w},0,${left / h},0,0,0,0,1,0,0,${yl},0,1)`;
      });
    }
    center(index) { this.offset = -index * this.pitch; this.velocity = 0; this.draw(); }
    down(e) {
      if (e.button !== 0 || !e.isPrimary) return;
      this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY, lastX: e.clientX, time: e.timeStamp, active: false };
      this.suppressClick = false;
      this.velocity = 0;
      this.start();
    }
    move(e) {
      if (!this.drag || e.pointerId !== this.drag.id) return;
      const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
      if (!this.drag.active && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 7) { this.drag = null; return; }
      if (!this.drag.active && Math.abs(dx) > 6) {
        this.drag.active = true;
        this.stage.setPointerCapture(e.pointerId);
      }
      if (this.drag.active) {
        if (e.cancelable) e.preventDefault();
        const delta = e.clientX - this.drag.lastX;
        const elapsed = Math.max(e.timeStamp - this.drag.time, 8);
        this.velocity = Math.max(-1800, Math.min(1800, delta / elapsed * 1000));
        this.offset += delta;
        this.suppressClick = true;
        this.draw();
      }
      this.drag.lastX = e.clientX;
      this.drag.time = e.timeStamp;
    }
    up(e) {
      if (!this.drag || e.pointerId !== this.drag.id) return;
      if (this.stage.hasPointerCapture(e.pointerId)) this.stage.releasePointerCapture(e.pointerId);
      if (e.type === 'pointercancel' || e.timeStamp - this.drag.time > 120) this.velocity = 0;
      this.drag = null;
      this.start();
    }
  }
  customElements.define('halloween-reel', HalloweenReel);
})();
